import * as React from "react";
import { styled, useTheme, alpha } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import CssBaseline from "@mui/material/CssBaseline";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Tooltip from "@mui/material/Tooltip";
import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import { LayoutDashboard, Users, Phone, UserCog, PhoneCall,
  Menu, ChevronLeft, LogOut, Moon, Sun, ShieldCheck, CreditCard, Brain, Receipt, Route, Layers, Coins, PhoneOutgoing } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../../actions/authSlice";
import { useNavigate } from "react-router-dom";
import { useColorMode } from "../../theme/ColorModeContext";
import { useTranslation } from "react-i18next";


import AdminDashboard   from "./pages/AdminDashboard";
import AiAdminUsage    from "./pages/AiAdminUsage";
import UserManagement   from "./pages/UserManagement";
import PhoneNumbersAdmin from "./pages/PhoneNumbersAdmin";
import SubAccountsAdmin  from "./pages/SubAccountsAdmin";
import CallLogsAdmin     from "./pages/CallLogsAdmin";
import AdminBilling      from "./pages/AdminBilling";
import RateCardsAdmin    from "./pages/RateCardsAdmin";
import ProviderRoutingAdmin from "./pages/ProviderRoutingAdmin";
import PlansAdmin        from "./pages/PlansAdmin";
import PriceCardAdmin    from "./pages/PriceCardAdmin";
import OutboundAdmin     from "./pages/OutboundAdmin";

const DRAWER_WIDTH     = 232;
const DRAWER_COLLAPSED = 64;
const ADMIN_GRADIENT   = "linear-gradient(135deg, #DC2626 0%, #EF4444 100%)";

const openedMixin  = (t) => ({ width: DRAWER_WIDTH,     overflowX: "hidden", transition: t.transitions.create("width", { easing: t.transitions.easing.sharp, duration: t.transitions.duration.enteringScreen }) });
const closedMixin  = (t) => ({ width: DRAWER_COLLAPSED, overflowX: "hidden", transition: t.transitions.create("width", { easing: t.transitions.easing.sharp, duration: t.transitions.duration.leavingScreen  }) });
const DrawerHeader = styled("div")(({ theme }) => ({ display: "flex", alignItems: "center", padding: theme.spacing(0, 1), ...theme.mixins.toolbar }));

const AppBar = styled(MuiAppBar, { shouldForwardProp: (p) => p !== "open" })(({ theme }) => ({
  zIndex: theme.zIndex.drawer + 1,
  color: theme.palette.text.primary,
  transition: theme.transitions.create(["width", "margin"], { easing: theme.transitions.easing.sharp, duration: theme.transitions.duration.leavingScreen }),
  variants: [{ props: ({ open }) => open, style: { marginLeft: DRAWER_WIDTH, width: `calc(100% - ${DRAWER_WIDTH}px)`, transition: theme.transitions.create(["width", "margin"], { easing: theme.transitions.easing.sharp, duration: theme.transitions.duration.enteringScreen }) } }],
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (p) => p !== "open" })(({ theme }) => ({
  width: DRAWER_WIDTH, flexShrink: 0, whiteSpace: "nowrap", boxSizing: "border-box",
  variants: [
    { props: ({ open }) =>  open, style: { ...openedMixin(theme), "& .MuiDrawer-paper": openedMixin(theme) } },
    { props: ({ open }) => !open, style: { ...closedMixin(theme), "& .MuiDrawer-paper": closedMixin(theme) } },
  ],
}));

const NAV_ITEMS = [
  { tkey: "navDashboard",    icon: LayoutDashboard, view: "dashboard"    },
  { tkey: "navUsers",        icon: Users,           view: "users"        },
  { tkey: "navSubAccounts",  icon: UserCog,         view: "subaccounts"  },
  { tkey: "navPhoneNumbers", icon: Phone,           view: "phonenumbers" },
  { tkey: "navCallLogs",     icon: PhoneCall,       view: "calllogs"     },
  { tkey: "navBilling",      icon: CreditCard,      view: "billing"      },
  { tkey: "navAiUsage",     icon: Brain,            view: "aiusage"      },
  { tkey: "navRateCards",   icon: Receipt,          view: "ratecards"    },
  { tkey: "navRouting",     icon: Route,            view: "routing"      },
  { tkey: "navPriceCard",   icon: Coins,            view: "pricecard"   },
  { tkey: "navPlans",       icon: Layers,           view: "plans"       },
  { tkey: "navOutbound",    icon: PhoneOutgoing,    view: "outbound"    },
];

const AdminShell = () => {
  const { t }    = useTranslation("admin");
  const theme    = useTheme();
  const { mode, toggleColorMode } = useColorMode();
  const isLight  = mode === "light";
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user     = useSelector((s) => s.auth.user);

  const [open,       setOpen]       = React.useState(false);
  const [activeView, setActiveView] = React.useState("dashboard");

  const initials = user?.name?.[0]?.toUpperCase() || "A";

  const iconBtnSx = {
    width: 34, height: 34, borderRadius: "10px", color: "text.secondary",
    background: isLight ? "rgba(255,255,255,0.7)" : "rgba(255,255,255,0.06)",
    border: "1px solid var(--line-200)", boxShadow: "none",
    "&:hover": { background: isLight ? "#FFFFFF" : "rgba(255,255,255,0.10)", boxShadow: "var(--shadow-card)" },
  };

  const handleLogout = () => { dispatch(logout()); navigate("/signin"); };

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />

      {/* ── AppBar ── */}
      <AppBar position="fixed" open={open} elevation={0}>
        <Toolbar sx={{ minHeight: { xs: 56, sm: 60 }, gap: 1 }}>
          <IconButton onClick={() => setOpen(true)} edge="start" sx={{ ...iconBtnSx, mr: 0.5, display: open ? "none" : "flex" }}>
            <Menu size={16} />
          </IconButton>

          {!open && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ width: 28, height: 28, borderRadius: "8px", background: ADMIN_GRADIENT, boxShadow: "0 2px 8px rgba(220,38,38,0.35)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <ShieldCheck size={13} color="white" />
              </Box>
              <Typography sx={{ fontWeight: 700, fontSize: "0.9375rem", color: "text.primary", letterSpacing: "-0.02em" }}>
                {t("adminPortalTitle")}
              </Typography>
              <Chip label="admin" size="small" sx={{ height: 18, fontSize: "0.625rem", fontWeight: 700, background: alpha("#DC2626", 0.1), color: "#DC2626", border: `1px solid ${alpha("#DC2626", 0.2)}` }} />
            </Box>
          )}

          <Box sx={{ flex: 1 }} />

          <IconButton onClick={toggleColorMode} size="small" sx={iconBtnSx}>
            {mode === "light" ? <Moon size={16} /> : <Sun size={16} />}
          </IconButton>

          <Tooltip title={user?.name || "Admin"} arrow>
            <Avatar sx={{ width: 34, height: 34, fontSize: "0.75rem", fontWeight: 700, background: ADMIN_GRADIENT, cursor: "pointer", boxShadow: "0 2px 6px rgba(220,38,38,0.3)", border: `2px solid ${isLight ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.12)"}` }}>
              {initials}
            </Avatar>
          </Tooltip>
        </Toolbar>
      </AppBar>

      {/* ── Drawer ── */}
      <Drawer variant="permanent" open={open}>
        <DrawerHeader sx={{ justifyContent: open ? "space-between" : "center", px: open ? 2 : 1, minHeight: "60px !important" }}>
          {open && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
              <Box sx={{ width: 30, height: 30, borderRadius: "9px", background: ADMIN_GRADIENT, boxShadow: "0 2px 8px rgba(220,38,38,0.35)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <ShieldCheck size={14} color="white" />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: "0.8125rem", color: "text.primary", lineHeight: 1.1 }} noWrap>{t("adminPortalTitle")}</Typography>
                <Typography sx={{ fontSize: "0.625rem", color: "#DC2626", fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase" }}>Teleredo</Typography>
              </Box>
            </Box>
          )}
          <IconButton onClick={() => setOpen(o => !o)} size="small" sx={{ width: 30, height: 30, borderRadius: "9px", color: "text.secondary", background: isLight ? "rgba(255,255,255,0.6)" : "rgba(255,255,255,0.06)", border: "1px solid var(--line-200)", "&:hover": { background: isLight ? "#FFFFFF" : "rgba(255,255,255,0.10)" } }}>
            {open ? (theme.direction === "rtl" ? null : <ChevronLeft size={16} />) : <Menu size={16} />}
          </IconButton>
        </DrawerHeader>

        <Divider />

        {open && (
          <Box sx={{ mx: 1.5, my: 1.5, px: 1.5, py: 1.25, borderRadius: "12px", background: isLight ? alpha("#DC2626", 0.04) : alpha("#DC2626", 0.08), border: `1px solid ${alpha("#DC2626", 0.15)}` }}>
            <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "text.primary" }} noWrap>{user?.name || t("administratorDefault")}</Typography>
            <Chip label={t("platformAdminChip")} size="small" sx={{ mt: 0.5, height: 16, fontSize: "0.6rem", fontWeight: 700, background: alpha("#DC2626", 0.1), color: "#DC2626" }} />
          </Box>
        )}

        {open && <Divider />}

        <List sx={{ px: 0.5, pt: 1, flex: 1 }}>
          {NAV_ITEMS.map(({ tkey, icon: Icon, view }) => {
            const isActive = activeView === view;
            return (
              <ListItem key={view} disablePadding sx={{ display: "block", mb: 0.25 }}>
                <Tooltip title={open ? "" : t(tkey)} placement="right" arrow disableHoverListener={open}>
                  <ListItemButton
                    selected={isActive}
                    onClick={() => setActiveView(view)}
                    sx={{
                      mx: 0.5, width: "calc(100% - 8px)", minHeight: 40, borderRadius: "10px",
                      justifyContent: open ? "flex-start" : "center", px: open ? 1.5 : 0,
                      background: isActive ? (isLight ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.07)") : "transparent",
                      border: isActive ? "1px solid var(--line-200)" : "1px solid transparent",
                      boxShadow: isActive ? "none" : "none",
                      transition: "all 0.15s ease",
                      "&:hover": { background: isActive ? (isLight ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.08)") : (isLight ? "rgba(220,38,38,0.05)" : "rgba(239,68,68,0.08)") },
                      "&.Mui-selected": { background: isLight ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.07)" },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 0, mr: open ? 1.5 : 0, justifyContent: "center", color: isActive ? "#DC2626" : "text.secondary" }}>
                      <Icon size={18} />
                    </ListItemIcon>
                    {open && (
                      <ListItemText
                        primary={t(tkey)}
                        primaryTypographyProps={{ fontSize: "0.875rem", fontWeight: isActive ? 700 : 500, color: isActive ? "#DC2626" : "text.secondary" }}
                      />
                    )}
                    {open && isActive && <Box sx={{ width: 6, height: 6, borderRadius: "50%", background: "#DC2626", ml: "auto", flexShrink: 0, boxShadow: `0 0 6px ${alpha("#DC2626", 0.5)}` }} />}
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            );
          })}
        </List>

        <Divider />

        <List sx={{ px: 0.5, py: 1 }}>
          <ListItem disablePadding sx={{ display: "block" }}>
            <Tooltip title={open ? "" : t("signOut")} placement="right" arrow disableHoverListener={open}>
              <ListItemButton
                onClick={handleLogout}
                sx={{ mx: 0.5, width: "calc(100% - 8px)", minHeight: 40, borderRadius: "10px", justifyContent: open ? "flex-start" : "center", px: open ? 1.5 : 0, background: "transparent", border: "1px solid transparent", transition: "all 0.15s ease", "&:hover": { background: alpha("#EF4444", 0.07), border: `1px solid ${alpha("#EF4444", 0.2)}` } }}
              >
                <ListItemIcon sx={{ minWidth: 0, mr: open ? 1.5 : 0, justifyContent: "center", color: "#EF4444" }}>
                  <LogOut size={18} />
                </ListItemIcon>
                {open && <ListItemText primary={t("signOut")} primaryTypographyProps={{ fontSize: "0.875rem", fontWeight: 600, color: "#EF4444" }} />}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        </List>

        {open && (
          <Box sx={{ px: 2.5, pb: 2 }}>
            <Typography variant="caption" sx={{ color: "text.disabled", fontSize: "0.6875rem" }}>
              Teleredo Admin v1.0
            </Typography>
          </Box>
        )}
      </Drawer>

      {/* ── Main content ── */}
      <Box component="main" sx={{ flexGrow: 1, background: (t) => t.palette.background.default, minHeight: "100vh", display: "flex", flexDirection: "column" }}>
        <DrawerHeader />
        <Box sx={{ flex: 1, p: { xs: 2.5, md: 3.5 } }}>
          {activeView === "dashboard"    && <AdminDashboard />}
          {activeView === "users"        && <UserManagement />}
          {activeView === "subaccounts"  && <SubAccountsAdmin />}
          {activeView === "phonenumbers" && <PhoneNumbersAdmin />}
          {activeView === "calllogs"     && <CallLogsAdmin />}
          {activeView === "billing"      && <AdminBilling />}
          {activeView === "aiusage"     && <AiAdminUsage />}
          {activeView === "ratecards"   && <RateCardsAdmin />}
          {activeView === "routing"     && <ProviderRoutingAdmin />}
          {activeView === "pricecard"   && <PriceCardAdmin />}
          {activeView === "outbound"    && <OutboundAdmin />}
          {activeView === "plans"       && <PlansAdmin />}
        </Box>
      </Box>
    </Box>
  );
};

export default AdminShell;
